import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { startCase } from 'lodash';
import { makeStyles } from 'tss-react/mui';
import {
  ActivityType,
  activityTypeColorRecord,
  otherActivityTypes,
  programActivityTypes,
} from '@shared/models/design/activities/activity-type.model';
import { ActivityMix } from '@shared/models/design/activities/activity-type.model';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import {
  getProgramDonutSegmentTooltipPlacement,
  getProgramDonutTooltipSlotProps,
} from './helpers/program-donut-tooltip.helpers';

const NIA_TOOLTIP = (
  <>
    <strong>Net Internal Area</strong>
    <br />
    Area excluding stairwells, facades, installations etc.
  </>
);

type ProgramDonutChartProps = {
  programs: ActivityMix;
  totalGfa: number;
  nia: number;
  compact?: boolean;
  innerRadius?: number;
  outerRadius?: number;
  onSegmentHoverChange?: (hovered: boolean) => void;
  /** When set (Map cards), segment tooltips share this anchor with the GFA tooltip. */
  tooltipAnchorEl?: HTMLElement | null;
};

type Segment = {
  path: string;
  hitPath: string;
  color: string;
  label: string;
  gfa: number;
  pct: number;
};

const formatArea = (value: number) =>
  new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(value);

const AREA_FONT_SIZE = 18;
const MIN_AREA_FONT_SIZE = 10;
const AREA_SIDE_GAP = 24;
const CORNER_RADIUS_PX = 4;
const SEGMENT_GAP_PX = 4;
const SEGMENT_BORDER = 'rgba(0, 0, 0, 0.15)';
const OTHER_HOVER_BORDER = 'rgba(0, 0, 0, 0.1)';
const OTHER_FILL = 'rgba(0, 0, 0, 0.1)';

const cx = 50;
const cy = 50;
const DEFAULT_OUTER_RADIUS = 46;
const DEFAULT_INNER_RADIUS = 24;

function polarToCartesian(
  angle: number,
  radius: number,
): { x: number; y: number } {
  const rad = (angle * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
}

function buildRoundedDonutPath(
  startAngle: number,
  endAngle: number,
  cornerRadius: number,
  innerRadius = DEFAULT_INNER_RADIUS,
  outerRadius = DEFAULT_OUTER_RADIUS,
): string {
  const span = endAngle - startAngle;
  if (span <= 0) return '';
  if (span >= 359.99 || cornerRadius <= 0) {
    return buildDonutPath(startAngle, endAngle, innerRadius, outerRadius);
  }

  const fits = (radius: number): boolean => {
    if (radius <= 0 || radius >= (outerRadius - innerRadius) / 2) return false;
    const outerAlpha =
      Math.asin(radius / (outerRadius - radius)) * (180 / Math.PI);
    const innerAlpha =
      Math.asin(radius / (innerRadius + radius)) * (180 / Math.PI);
    const outerTangent =
      (outerRadius - radius) * Math.cos((outerAlpha * Math.PI) / 180);
    const innerTangent =
      (innerRadius + radius) * Math.cos((innerAlpha * Math.PI) / 180);
    return (
      outerAlpha * 2 < span &&
      innerAlpha * 2 < span &&
      outerTangent > innerTangent
    );
  };

  let radius = cornerRadius;
  while (!fits(radius) && radius > 0.05) radius *= 0.8;
  if (!fits(radius))
    return buildDonutPath(startAngle, endAngle, innerRadius, outerRadius);

  const outerAlpha =
    Math.asin(radius / (outerRadius - radius)) * (180 / Math.PI);
  const innerAlpha =
    Math.asin(radius / (innerRadius + radius)) * (180 / Math.PI);
  const outerTangentRadius =
    (outerRadius - radius) * Math.cos((outerAlpha * Math.PI) / 180);
  const innerTangentRadius =
    (innerRadius + radius) * Math.cos((innerAlpha * Math.PI) / 180);

  const outerArcStart = polarToCartesian(startAngle + outerAlpha, outerRadius);
  const outerArcEnd = polarToCartesian(endAngle - outerAlpha, outerRadius);
  const innerArcEnd = polarToCartesian(endAngle - innerAlpha, innerRadius);
  const innerArcStart = polarToCartesian(startAngle + innerAlpha, innerRadius);
  const outerStartRay = polarToCartesian(startAngle, outerTangentRadius);
  const outerEndRay = polarToCartesian(endAngle, outerTangentRadius);
  const innerEndRay = polarToCartesian(endAngle, innerTangentRadius);
  const innerStartRay = polarToCartesian(startAngle, innerTangentRadius);
  const largeOuter = endAngle - startAngle - outerAlpha * 2 > 180 ? 1 : 0;
  const largeInner = endAngle - startAngle - innerAlpha * 2 > 180 ? 1 : 0;
  const fillet = (to: { x: number; y: number }) =>
    `A ${radius} ${radius} 0 0 1 ${to.x} ${to.y}`;

  return [
    `M ${outerArcStart.x} ${outerArcStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeOuter} 1 ${outerArcEnd.x} ${outerArcEnd.y}`,
    fillet(outerEndRay),
    `L ${innerEndRay.x} ${innerEndRay.y}`,
    fillet(innerArcEnd),
    `A ${innerRadius} ${innerRadius} 0 ${largeInner} 0 ${innerArcStart.x} ${innerArcStart.y}`,
    fillet(innerStartRay),
    `L ${outerStartRay.x} ${outerStartRay.y}`,
    fillet(outerArcStart),
    'Z',
  ].join(' ');
}
function buildDonutPath(
  startAngle: number,
  endAngle: number,
  innerRadius = DEFAULT_INNER_RADIUS,
  outerRadius = DEFAULT_OUTER_RADIUS,
): string {
  const outerStart = polarToCartesian(startAngle, outerRadius);
  const outerEnd = polarToCartesian(endAngle, outerRadius);
  const innerStart = polarToCartesian(startAngle, innerRadius);
  const innerEnd = polarToCartesian(endAngle, innerRadius);
  if (endAngle - startAngle >= 359.99) {
    const outerMid = polarToCartesian(startAngle + 180, outerRadius);
    const innerMid = polarToCartesian(startAngle + 180, innerRadius);
    return [
      `M ${outerStart.x} ${outerStart.y}`,
      `A ${outerRadius} ${outerRadius} 0 1 1 ${outerMid.x} ${outerMid.y}`,
      `A ${outerRadius} ${outerRadius} 0 1 1 ${outerStart.x} ${outerStart.y}`,
      `L ${innerStart.x} ${innerStart.y}`,
      `A ${innerRadius} ${innerRadius} 0 1 0 ${innerMid.x} ${innerMid.y}`,
      `A ${innerRadius} ${innerRadius} 0 1 0 ${innerStart.x} ${innerStart.y}`,
      'Z',
    ].join(' ');
  }
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

export const ProgramDonutChart: React.FC<ProgramDonutChartProps> = ({
  programs,
  totalGfa,
  nia,
  compact = false,
  onSegmentHoverChange,
  tooltipAnchorEl,
  innerRadius = compact ? DEFAULT_INNER_RADIUS * 0.75 : DEFAULT_INNER_RADIUS,
  outerRadius = DEFAULT_OUTER_RADIUS,
}) => {
  const { classes } = useStyles();
  const chartId = useId();
  const holeRef = useRef<HTMLDivElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLElement>(null);
  const efficiencyRef = useRef<HTMLElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [pxToUnit, setPxToUnit] = useState(0.5);
  const [areaFontSize, setAreaFontSize] = useState(AREA_FONT_SIZE);
  const [chartElement, setChartElement] = useState<HTMLDivElement | null>(null);
  const areaLabel = getDisplayNumber(nia);
  const tooltipAnchor = tooltipAnchorEl ?? chartElement;

  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const measure = () => {
      const width = svg.getBoundingClientRect().width;
      if (width <= 0) return;
      setPxToUnit(100 / width);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(svg);
    return () => observer.disconnect();
  }, []);

  const segments = useMemo(() => {
    if (totalGfa <= 0) return [];

    const items: { gfa: number; color: string; label: string }[] = [];

    // Named program segments
    for (const type of programActivityTypes) {
      const gfa = programs[type] ?? 0;
      if (gfa > 0) {
        items.push({
          gfa,
          color: activityTypeColorRecord[type],
          label: type,
        });
      }
    }

    // All other activity types collapsed into one "Other" gray segment
    const otherGfa = otherActivityTypes.reduce(
      (sum, type) => sum + (programs[type as ActivityType] ?? 0),
      0,
    );
    if (otherGfa > 0) {
      items.push({
        gfa: otherGfa,
        color: OTHER_FILL,
        label: 'Other',
      });
    }

    if (items.length === 0) return [];

    const namedGfa = items.reduce(
      (sum, item) => sum + (item.label === 'Other' ? 0 : item.gfa),
      0,
    );
    // The SVG group rotates -90°, so Other's midpoint at 180° lands below
    // the centre. Offset every segment together to preserve their order.
    let currentAngle =
      otherGfa > 0 ? 180 - ((namedGfa + otherGfa / 2) / totalGfa) * 360 : 0;
    const gapAngle =
      items.length > 1
        ? (((compact ? 2 : SEGMENT_GAP_PX) * pxToUnit) / outerRadius) *
          (180 / Math.PI)
        : 0;
    const builtSegments: Segment[] = items.map((item) => {
      const angle = (item.gfa / totalGfa) * 360;
      const inset = Math.min(gapAngle / 2, Math.max(0, (angle - 1) / 2));
      const path = buildRoundedDonutPath(
        currentAngle + inset,
        currentAngle + angle - inset,
        (compact ? 2 : CORNER_RADIUS_PX) * pxToUnit,
        innerRadius,
        outerRadius,
      );
      const hitPath = buildDonutPath(
        currentAngle,
        currentAngle + angle,
        innerRadius,
        outerRadius,
      );
      currentAngle += angle;
      return {
        path,
        hitPath,
        color: item.color,
        label: item.label,
        gfa: item.gfa,
        pct: Math.round((item.gfa / totalGfa) * 100),
      };
    });

    return builtSegments;
  }, [programs, totalGfa, pxToUnit, compact, innerRadius, outerRadius]);

  useLayoutEffect(() => {
    const hole = holeRef.current;
    const area = areaRef.current;
    if (!hole || !area) return;

    const fit = () => {
      const available = hole.clientWidth - AREA_SIDE_GAP;
      let size = AREA_FONT_SIZE;
      area.style.fontSize = `${size}px`;
      while (size > MIN_AREA_FONT_SIZE && area.scrollWidth > available) {
        size -= 1;
        area.style.fontSize = `${size}px`;
      }
      setAreaFontSize((current) => (current === size ? current : size));

      const label = labelRef.current;
      const efficiency = efficiencyRef.current;
      if (!label || !efficiency) return;
      const holeRect = hole.getBoundingClientRect();
      const areaRect = area.getBoundingClientRect();
      const roomAbove = areaRect.top - holeRect.top;
      const roomBelow = holeRect.bottom - areaRect.bottom;
      const nudgeTowardArea = 6;
      label.style.marginBottom = `${Math.max(0, (roomAbove - label.offsetHeight) / 2 - nudgeTowardArea)}px`;
      efficiency.style.bottom = `${Math.max(0, (roomBelow - efficiency.offsetHeight) / 2 + nudgeTowardArea)}px`;
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(hole);
    return () => observer.disconnect();
  }, [areaLabel, segments.length]);

  if (segments.length === 0 && !compact) return null;

  const efficiencyPct = totalGfa > 0 ? Math.round((nia / totalGfa) * 100) : 0;
  // Compact Map cards keep tooltips under the chart; Design allows left flip.
  const donutTooltipSlotProps = getProgramDonutTooltipSlotProps(tooltipAnchor, {
    disableFlip: compact,
    outerRadiusRatio: compact ? outerRadius / 100 : undefined,
  });
  const segmentTooltipPlacement =
    getProgramDonutSegmentTooltipPlacement(compact);

  return (
    <Box className={classes.root} style={compact ? { padding: 0 } : undefined}>
      <Box ref={setChartElement} className={classes.chart}>
        <svg
          ref={svgRef}
          className={classes.svg}
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <filter
              id="program-donut-hover-shadow"
              x="-80%"
              y="-80%"
              width="260%"
              height="260%"
            >
              <feDropShadow
                dx="0"
                dy="1"
                stdDeviation="3"
                floodColor="#000"
                floodOpacity="0.05"
              />
            </filter>
          </defs>
          {compact && segments.length === 0 && (
            <circle
              cx={cx}
              cy={cy}
              r={(innerRadius + outerRadius) / 2}
              fill="none"
              stroke={OTHER_FILL}
              strokeWidth={outerRadius - innerRadius}
            />
          )}
          <g transform="rotate(-90 50 50)">
            {[...segments]
              .sort((a) => (a.label === 'Other' ? -1 : 1))
              .map((seg) => (
                <Tooltip
                  key={seg.label}
                  title={
                    <>
                      <strong>{startCase(seg.label)}</strong>{' '}
                      {`${formatArea(seg.gfa)} m²`}
                      {!compact && (
                        <>
                          <br />
                          {`${seg.pct}% of GFA`}
                        </>
                      )}
                    </>
                  }
                  placement={segmentTooltipPlacement}
                  slotProps={donutTooltipSlotProps}
                >
                  <g
                    className={classes.segment}
                    onMouseEnter={() => onSegmentHoverChange?.(true)}
                    onMouseLeave={() => onSegmentHoverChange?.(false)}
                  >
                    <path
                      d={seg.path}
                      fill={seg.color}
                      style={{ pointerEvents: 'none' }}
                    />
                    <path d={seg.hitPath} fill="transparent" />
                    {seg.label !== 'Other' ? (
                      <>
                        <clipPath id={`${chartId}-program-donut-${seg.label}`}>
                          <path d={seg.path} />
                        </clipPath>
                        <path
                          d={seg.path}
                          fill="none"
                          stroke={SEGMENT_BORDER}
                          strokeWidth={2}
                          vectorEffect="non-scaling-stroke"
                          clipPath={`url(#${chartId}-program-donut-${seg.label})`}
                          style={{ pointerEvents: 'none' }}
                        />
                      </>
                    ) : (
                      <>
                        <clipPath id={`${chartId}-program-donut-Other`}>
                          <path d={seg.path} />
                        </clipPath>
                        <path
                          className={classes.otherBorder}
                          data-other-border=""
                          d={seg.path}
                          fill="none"
                          strokeWidth={2}
                          vectorEffect="non-scaling-stroke"
                          clipPath={`url(#${chartId}-program-donut-Other)`}
                          style={{ pointerEvents: 'none' }}
                        />
                      </>
                    )}
                  </g>
                </Tooltip>
              ))}
          </g>
        </svg>
        {!compact && (
          <Box ref={holeRef} className={classes.hole}>
            <Tooltip
              title={NIA_TOOLTIP}
              placement="left"
              slotProps={donutTooltipSlotProps}
            >
              <Box className={classes.nia}>
                <Typography ref={labelRef} className={classes.niaLabel}>
                  NIA
                </Typography>
                <Box
                  ref={areaRef}
                  className={classes.niaValue}
                  style={{ fontSize: areaFontSize }}
                >
                  <Typography component="span" className={classes.niaNumber}>
                    {areaLabel}
                  </Typography>
                  <Typography component="span" className={classes.niaUnit}>
                    m²
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
            <Tooltip
              title={
                <>
                  <strong>Efficiency</strong>
                  <br />
                  NIA to GFA ratio
                </>
              }
              placement="left"
              slotProps={donutTooltipSlotProps}
            >
              <Typography ref={efficiencyRef} className={classes.efficiency}>
                {efficiencyPct}%
              </Typography>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(1, 2, 2),
    overflow: 'visible',
  },
  chart: {
    position: 'relative',
    overflow: 'visible',
  },
  svg: {
    width: '100%',
    height: 'auto',
    aspectRatio: '1 / 1',
    display: 'block',
    overflow: 'visible',
  },
  segment: {
    cursor: 'pointer',
    '&:hover': {
      filter: 'url(#program-donut-hover-shadow)',
    },
    '&:hover [data-other-border]': {
      stroke: OTHER_HOVER_BORDER,
    },
  },
  otherBorder: {
    stroke: 'transparent',
  },
  hole: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48%',
    aspectRatio: '1 / 1',
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  },
  nia: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'auto',
    cursor: 'default',
  },
  niaLabel: {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    marginBottom: 0,
    transform: 'translateX(-50%)',
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: '0.7px',
    lineHeight: 1.56,
    textTransform: 'uppercase',
  },
  niaValue: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 2,
    marginRight: -4,
    fontSize: AREA_FONT_SIZE,
    whiteSpace: 'nowrap',
  },
  niaNumber: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: '1em',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  niaUnit: {
    color: 'rgba(0, 0, 0, 0.3)',
    fontSize: '1em',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  efficiency: {
    position: 'absolute',
    bottom: '20%',
    left: '50%',
    pointerEvents: 'auto',
    cursor: 'default',
    transform: 'translateX(-50%)',
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: '0.7px',
    lineHeight: 1.56,
  },
}));
